import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { buildToMeRows } from "../Approval/Index";
import dayjs from "dayjs";
import { STATUS, TYPE } from "../../design/ui";
import {
  normalizeTask,
  fmtDateTime,
  STATUS_LABEL,
  LABEL_TO_STATUS,
  STATUS_CODE,
  PRIO_LABEL,
  statusKey,
  reviewStatusKey,
  typeKey,
  toPlainText,
  mentionHtml,
  fmtRelative,
} from "../../design/domain";
import { useApp } from "../../design/AppContext";
import { isReportingRole } from "../../hook/useDailyTaskReminder";
import RichEditor from "../../components/design/RichEditor";
import RichView from "../../components/design/RichView";
import TaskDrawer from "../../components/task/TaskDrawer";
import { apiGet, apiPostForm, apiPostJson, apiPutJson, apiDelete } from "../../utils/api";
import { jwtEncode } from "../../utils/jwt";
import { apiErrorMessage } from "../../utils/apiError";
import request from "../../utils/request";
import { getToken } from "../../utils/token";
import ProgressChoice from "../../components/design/ProgressChoice";
import { Banner, DataRow, Disclosure, EmptyState, Progress, RowButton, StatusChip } from "../../components/mui";
import { SkeletonRows } from "../../components/loading";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormHelperText from "@mui/material/FormHelperText";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  MdAdd,
  MdCheck,
  MdCircle,
  MdErrorOutline,
  MdInfoOutline,
  MdRadioButtonUnchecked,
  MdRemove,
  MdWarningAmber,
} from "react-icons/md";

/**
 * 我手上的工項（設計稿 #screen-tasks，el-pm-template_1.html L1095-L1300）
 *
 * 版面是「左邊一串我的工項、右邊回報這一件」，中間沒有第三層。
 * 改版前這一頁把六個狀態分頁、分頁器、流程指示器、狀態下拉、滑桿、Git 欄位、
 * 驗收條件、附件、留言、歷程全部同時攤在畫面上 —— 一個人一天要用十幾次的頁面，
 * 每次卻都要先在二十幾個框框裡找到「這次做了什麼」那一格。
 *
 * 這一版的取捨：
 *   · 主線只有三件事 —— 選一件、做到幾成、這次做了什麼。
 *   · 主線以外的既有功能一件都沒有刪，改成收進收合區（Disclosure）
 *     （改狀態／解決結論／推進／程式碼位置／驗收條件／以前的紀錄／留言／附件）。
 *   · 已完成的工項不再給整套表單，只顯示結果（設計稿 L1259-L1270）。
 *
 * 版面規則（src/styles/README.md）：
 *   · 外層不另包頁面容器，Layout.js 的內容區已經處理寬度與留白。
 *   · 整頁只有一顆主要按鈕（contained），留給「送出回報」。
 *   · 顏色一律 token、字級一律 --t-*，不寫死 px，也不自己寫 max-width。
 *
 * 資料來源與寫入路徑與改版前完全相同（一支都沒有換）：
 *   GET_MyTaskList（/MyTask/List）／GET /MyTask/Progress/{taskId}／POST /MyTask/Progress
 *   ／GET /Review/List／PUT /Review/Criterion／/Workflow/Transitions・Transition・Resolutions
 *   ／/Task/Comment*／/Task/Attachment*／/MyTask/Progress/Download/{id}
 * 唯一多的一支是 GET /MyTask/Today —— 頁首那條「今天還沒有紀錄」與底部那張
 * 「今天的紀錄」都要知道「今天回報過沒有」，而那個答案只有這支算得出來
 * （與全站日報提醒 hook/useDailyTaskReminder.js 用的是同一支，兩處口徑一致）。
 */

/* ===================== 分頁 ===================== */

/**
 * 三個分頁（設計稿 L1109-L1113）。原本六個狀態膠囊縮成三個問題：
 *   今天要做的 ＝ 待處理＋進行中（球在我這裡，現在就動得了的）
 *   在等別人   ＝ 審核中（已經交出去，等把關者決議，我改不動）
 *   做完了     ＝ 已完成＋已關閉（兩者在全站統計裡本來就當同一件事）
 *
 * 少掉的兩個分頁沒有把資料藏起來：
 *   · 「逾期」不是狀態而是期限，逾期的工項仍在「今天要做的」裡，而且整列會掛
 *     紅色的「已逾期」徽章（tagOf）—— 比多一個要自己去點的分頁更看得到。
 *   · 「已關閉」併進「做完了」：它是合法狀態、資料還在，只是不在正常流程上。
 */
const TABS = [
  ["todo", "待辦"],
  ["waiting", "等待中"],
  ["done", "已完成"],
];

const TAB_STATUS = {
  todo: ["pending", "progress"],
  waiting: ["review"],
  done: ["done", "closed"],
};

/** 左欄先給幾列；其餘用一顆鈕展開（見下面那顆「還有 N 件」的說明） */
const LIST_MAX = 8;

/** 「待我審核」區先列幾件；更多的到審核清單頁看 */
const REVIEW_MAX = 5;

/** 收合區標頭右側的字樣：照開合狀態寫該按了會怎樣 */
const foldText = (open) => (open ? "收合" : "展開");

/**
 * 「推進」鈕的下一站與字樣。
 *
 * 「已完成」是終點，沒有下一站 —— 原本是 done → closed（關閉），
 * 但「已關閉」在這套系統裡什麼都沒買到：所有統計（里程碑狀態、專案進度、
 * 吞吐量、逾期判定…）一律把已完成與已關閉當同一件事，而它要求填的
 * 解決結論從頭到尾沒有任何地方讀出來。
 *
 * 已關閉仍保留成一個合法狀態（既有資料要能顯示、狀態下拉也還在），
 * 只是不再是正常流程的一站；舊的已關閉工項仍可用「重啟」拉回進行中。
 *
 * 「審核中」也沒有下一站了 —— 原本是 review → done「完成」。審核通過的那一刻，
 * 後端 ReviewService.Decide 就自己把工項推到「已完成」；審核通過之前按「完成」，
 * 又一定被 WorkflowService.EvaluateDoneGuard 擋下（沒有通過的審核單）。
 * 所以這顆鈕在正常流程裡只會做兩件事：按了失敗，或讓人以為核准之後還要回來按一次。
 * 審核通過卻還停在審核中的舊資料另外處理，見下面的 approvedButStuck。
 */
const NEXT_MAP = { pending: "progress", progress: "review", closed: "progress" };
const NEXT_LABEL = { pending: "開始", progress: "送審", closed: "重啟" };

/**
 * 更新狀態下拉選項。
 *
 * 不含「已完成」是這一頁自己的取捨：完成該是有人看過之後的結果，不是回報的人
 * 自己宣告的，所以不在下拉裡開「勾一勾驗收條件就直接完成」這條後門。
 *
 * 後端站在同一邊：Web/Services/WorkflowService.cs 的 EvaluateDoneGuard 規定
 * 「進入已完成一律要先有通過的審核」，就算有人繞過這個下拉直接打
 * /Workflow/Transition 也會被擋下。這裡少一個選項只是不要讓人白按一次。
 */
const STATUS_OPTS = ["待處理", "進行中", "審核中", "已關閉"];

/** 後端統一以 res_msg 回傳失敗原因（守門不通過的理由也在這裡）；403 之類沒有 body 的另外補說明 */
const errMsg = (e) => apiErrorMessage(e, _.get(e, "message") || "操作失敗");

/** 附件大小：附件列很窄，只要看得出「這是不是個大檔」就夠了 */
const fmtAttSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (!n) return "—";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${parseFloat((n / 1024 ** i).toFixed(1))} ${units[i]}`;
};

/* ===================== 日期與徽章 =====================
 *
 * typeLabelOf / isSameDay / isLate / isDueToday / tagOf / dueLabel 是全站「我的工項」的
 * 判斷基準：其他頁（看板、跨專案清單、專案工項、時程）的註解寫「與 pages/MyTask/Index.js
 * 一致」指的就是這幾支。要改規則時，那幾頁的同名函式要一起改。
 */

/**
 * 型別徽章的文字。
 *
 * typeKey() 認不得時一律退回 "task"，所以不能無條件貼徽章：原始資料沒帶型別時
 * 一整排工項都會被標成「工作」，那比沒有徽章更誤導。只有真的帶了型別欄位才貼。
 */
const TYPE_FIELD_KEYS = ["issue_type_key", "issueTypeKey", "issue_type", "IssueTypeKey", "type"];
function typeLabelOf(raw) {
  const k = TYPE_FIELD_KEYS.find((f) => _.get(raw, f));
  if (!k) return "";
  return (TYPE[typeKey(_.get(raw, k))] || TYPE.task).label;
}

/**
 * 「今天」一律用當地時區的日界判斷。
 * 後端的 due_date 是 DateTimeOffset（帶時區），直接比 UTC 的話，
 * 台北時間今天早上 7 點以前到期的工項會被算成昨天。
 */
function isSameDay(v, base) {
  if (!v) return false;
  const d = dayjs(v);
  return d.isValid() && d.isSame(base, "day");
}

/**
 * 真的逾期 ＝ 到期日已經過了「今天」這一天。
 *
 * 不能直接用 normalizeTask 的 overdue，也不能用後端的 is_overdue：
 * 兩邊都是 `due_date < 現在時刻`，而 due_date 存的是當天 00:00，
 * 所以今天到期的工項在今天早上八點就會被判成逾期 —— 左邊清單標「已逾期」，
 * 右邊卻寫著「今天要交」。
 */
function isLate(t, base) {
  return !!t.overdue && !isSameDay(t.dueRaw, base);
}

/** 今天到期、而且還沒做完（done/closed 的守門不能省，否則做完的列也會標紅） */
function isDueToday(t, base) {
  const k = statusKey(t.status);
  if (k === "done" || k === "closed") return false;
  return isSameDay(t.dueRaw, base);
}

/** 狀態 → StatusChip 的 kind 與前綴符號 */
const STATUS_TAG = {
  pending: { kind: "closed", mark: <MdRemove /> },
  progress: { kind: "progress", mark: <MdCircle size="0.6em" /> },
  review: { kind: "warn", mark: <MdErrorOutline /> },
  done: { kind: "done", mark: <MdCheck /> },
  closed: { kind: "closed", mark: <MdRemove /> },
};

/**
 * 右側徽章：逾期與今天到期一律搶下 attention，其餘才顯示狀態。
 * 一列只能講一件事，狀態（進行中）跟期限（今天要交）同時出現的話，
 * 真正要看的那個會被稀釋掉。
 */
function tagOf(t, base) {
  if (isDueToday(t, base)) return { kind: "attention", mark: <MdWarningAmber />, text: "今日到期" };
  if (isLate(t, base)) return { kind: "attention", mark: <MdWarningAmber />, text: "已逾期" };
  const k = statusKey(t.status);
  const s = STATUS_TAG[k] || STATUS_TAG.pending;
  return { kind: s.kind, mark: s.mark, text: (STATUS[k] || STATUS.pending).label };
}

/** 到期日文案：今天的補上「今天」兩個字，今年的寫「9 月 30 日」，跨年才補年份 */
function dueLabel(v, base) {
  if (!v) return "";
  const d = dayjs(v);
  if (!d.isValid()) return "";
  if (d.isSame(base, "day")) return `今天 ${d.format("M/D")}`;
  if (d.isSame(base, "year")) return d.format("M 月 D 日");
  return d.format("YYYY 年 M 月 D 日");
}

/* ===================== 這一頁自己的小工具 ===================== */

const WEEK = ["日", "一", "二", "三", "四", "五", "六"];

/** 頁首的「9 月 21 日 星期一」。沒有載 dayjs 的 zh-TW locale，星期自己對表就好 */
function todayTitle(base) {
  return `${base.format("M 月 D 日")} 星期${WEEK[base.day()]}`;
}

/** 日報那張卡的時間只要「幾點幾分」—— 同一張卡裡的每一筆都是今天 */
function fmtClock(v) {
  const d = dayjs(v);
  return d.isValid() ? d.format("HH:mm") : "";
}

/**
 * 純文字 → 可以安全丟進 RichView 的 HTML。
 *
 * 進度回報的內容一直是以 HTML 存的（原本的輸入是富文本編輯器），而 RichView 是
 * dangerouslySetInnerHTML，本身沒有消毒。主要輸入改成一般輸入格之後，
 * 使用者打的「<」「&」如果原樣送出去，輕則畫面吃掉半句話，重則變成注入。
 * 所以一律先跳脫、再把換行接回 <br> —— 跳脫方式與 domain.js 的 mentionHtml 同一套。
 */
function textToHtml(text) {
  const esc = String(text || "").replace(
    /[&<>"]/g,
    (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch])
  );
  return esc.replace(/\r?\n/g, "<br>");
}

function App(props) {
  const { toast, userInfo } = useApp();
  const navigate = useNavigate();

  /* null = 使用者還沒點過分頁，由 curTab 自動挑；點過就以他點的為準 */
  const [myTab, setMyTab] = useState(null);
  const [showAll, setShowAll] = useState(false);
  /* 摘要列的快速篩選："" 不篩、"due" 今日到期、"late" 已逾期（只看還沒做完的，跨分頁） */
  const [quick, setQuick] = useState("");
  const [selId, setSelId] = useState(null);
  const [dod, setDod] = useState([]);
  /* /Review/List 同一次回應裡的審核單。做完的工項要講「誰審的」，靠的就是它 */
  const [reviews, setReviews] = useState([]);
  /** /Review/List 的 approval_effective：這一輪算不算通過（通過後重新打開過的不算）。null = 後端沒給 */
  const [approvalEffective, setApprovalEffective] = useState(null);
  const [logs, setLogs] = useState([]);
  /*
   * 進度歷程的載入狀態。取失敗時不能照樣顯示「還沒有任何紀錄」——那是在說資料不存在，
   * 其實只是這次沒拿到。logsTaskId 是目前這份 logs 屬於哪一筆工項（成功或失敗都算載完）。
   */
  const [logsError, setLogsError] = useState(false);
  const [logsTaskId, setLogsTaskId] = useState(null);
  const logsReq = useRef(0);
  // 非同步回來後要比對「現在選的是哪筆」，state 閉包拿到的是送出當下的舊值
  const selIdRef = useRef(selId);
  selIdRef.current = selId;
  const [atts, setAtts] = useState([]);
  /* 留言 —— 與工項詳情同一份資料（/Task/Comment），RD 不必為了回一句話跳到另一頁 */
  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [rStatus, setRStatus] = useState("");
  const [rPct, setRPct] = useState("");
  const [rGit, setRGit] = useState("");
  /* 主要輸入是純文字；rHtml 只有切到完整編輯器（要貼圖）時才用得到 */
  const [rText, setRText] = useState("");
  const [rHtml, setRHtml] = useState("");
  const [richMode, setRichMode] = useState(false);
  const [textErr, setTextErr] = useState("");
  /* 「自己填幾 %」：五顆大按鈕只有五段，任意百分比的能力要留著 */
  /** 回報可夾一個檔案（截圖、log…）—— 後端本來就收得下 */
  const [rFile, setRFile] = useState(null);
  const rFileRef = useRef(null);
  /*
   * 解決結論。「已關閉」這條轉換的守門要求填「為什麼收掉」，不填就是
   * 「操作失敗: 需選擇解決結論」。needRes 由該工項當下真正的轉換設定決定，
   * 不寫死狀態名稱（工作流是可以改的）。
   */
  const [resolutions, setResolutions] = useState([]);
  const [rResId, setRResId] = useState("");
  const [needRes, setNeedRes] = useState(false);
  /*
   * 三個收合區做成受控的：做完的工項那張結果卡上有「看當時寫了什麼」與「補一句說明」，
   * 按了要真的把對應的收合區打開，否則那兩顆鈕按下去毫無反應。
   */
  const [moreOpen, setMoreOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [cmtOpen, setCmtOpen] = useState(false);
  /* 另外兩個收合區（完成條件、附件）也受控，標頭右側的「展開／收合」字樣才知道該寫哪一個 */
  const [dodOpen, setDodOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const cmtRef = useRef(null);
  /*
   * 「今天回報過沒有」。GET /MyTask/Today 回
   * { date, is_workday, has_task, task_count, progress_count }，
   * 是全站日報提醒（hook/useDailyTaskReminder.js）用的同一支。
   * dayTick 用來在送出回報之後重問一次，橫幅才會自己消失、底部的筆數才跟得上。
   */
  const [dayStat, setDayStat] = useState(null);
  const [dayTick, setDayTick] = useState(0);
  /*
   * 「看完整工項」展開的抽屜。
   *
   * 不導到專案詳情頁 —— 這一欄是「回報這一件」，使用者常常是回報打到一半想回頭
   * 確認需求才按它，一跳頁填好的字就全沒了（回上一頁也救不回來）。
   * 抽屜蓋在原頁上，關掉就回到剛剛填的狀態。
   */
  const [drawerTask, setDrawerTask] = useState(null);
  // 送出中以 ref 擋重複點擊
  const submitting = useRef(false);

  const reload = useCallback(() => {
    props.GET_MyTaskList(null, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* 待我審核的件數：摘要列與側欄紅點共用 state.Approval.ToMe（首頁合併前由「今天」頁抓） */
  useEffect(() => {
    props.GET_ApprovalToMe(null, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    let alive = true;
    apiGet("/MyTask/Today")
      .then((d) => {
        if (alive) setDayStat(d || null);
      })
      .catch(() => {
        // 查不到就當作沒有答案：提醒本身不該再變成一個擋在使用者面前的錯誤
        if (alive) setDayStat(null);
      });
    return () => {
      alive = false;
    };
  }, [dayTick]);

  /* ---------- 今天 ---------- */

  /*
   * 用日期字串當 memo 的依賴而不是 dayjs 物件：dayjs() 每次 render 都是新物件，
   * 放進依賴陣列等於每次都重算，而日期字串只有跨日才會變 ——
   * 分頁開著過午夜也能自己更新。
   */
  const todayKey = dayjs().format("YYYY-MM-DD");
  const today = useMemo(() => dayjs(todayKey).startOf("day"), [todayKey]);

  /* ---------- 資料正規化 ---------- */

  // 後端 /MyTask/List 只回 snake_case 的精簡欄位，一律過 normalizeTask 補齊設計稿需要的形狀
  const rawTasks = _.get(props, "myTasks", []);
  const tasks = useMemo(() => (rawTasks || []).map((t) => normalizeTask(t)), [rawTasks]);

  // /MyTask/List 不回指派人姓名（清單本身就是「我的」），頁首的名字退回登入者姓名
  const meName =
    _.get(userInfo, "name") ||
    _.get(userInfo, "Name") ||
    _.get(userInfo, "account") ||
    _.get(userInfo, "Account") ||
    "";

  const reviewRows = useMemo(() => buildToMeRows(props.toMeRaw), [props.toMeRaw]);
  const countOf = (k) => tasks.filter((t) => TAB_STATUS[k].includes(t.status)).length;

  /* 還沒點過分頁時一律停在「今天要做的」—— 這一頁就是用來看「我現在該動哪一件」的 */
  const curTab = myTab || "todo";

  /*
   * 排序：最可能今天爆掉的排最前面（逾期 → 今天到期 → 有到期日、近的在前 → 沒有到期日），
   * 「做完了」反過來，剛做完的排最前面 —— 那個分頁的用途是「找回剛剛那一件」。
   */
  const openStatuses = [...TAB_STATUS.todo, ...TAB_STATUS.waiting];
  const dueCount = tasks.filter((t) => openStatuses.includes(t.status) && isDueToday(t, today)).length;
  const lateCount = tasks.filter((t) => openStatuses.includes(t.status) && isLate(t, today)).length;

  const mineList = useMemo(() => {
    /* 摘要列的篩選優先：跨分頁看「還沒做完」裡今日到期／已逾期的那幾件 */
    const list = quick
      ? tasks.filter(
          (t) =>
            [...TAB_STATUS.todo, ...TAB_STATUS.waiting].includes(t.status) &&
            (quick === "due" ? isDueToday(t, today) : isLate(t, today))
        )
      : tasks.filter((t) => (TAB_STATUS[curTab] || []).includes(t.status));
    if (curTab === "done") {
      return list.sort((a, b) => {
        const av = a.completedAt ? dayjs(a.completedAt).valueOf() : 0;
        const bv = b.completedAt ? dayjs(b.completedAt).valueOf() : 0;
        return bv - av;
      });
    }
    const rank = (t) => {
      if (isLate(t, today)) return 0;
      if (isSameDay(t.dueRaw, today)) return 1;
      return t.dueRaw ? 2 : 3;
    };
    return list.sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      if (a.dueRaw && b.dueRaw) return dayjs(a.dueRaw).valueOf() - dayjs(b.dueRaw).valueOf();
      return 0;
    });
  }, [tasks, curTab, today, quick]);

  const visibleList = showAll ? mineList : mineList.slice(0, LIST_MAX);
  const hiddenCount = mineList.length - visibleList.length;

  /* ---------- 選取的工項 ---------- */

  const selTask = tasks.find((t) => t.id === selId) || null;
  const curKey = selTask ? selTask.status : "pending";
  const curLabel = STATUS_LABEL[curKey];
  /* 做完的工項只顯示結果，不再給進度、狀態與整套表單（設計稿 L1259-L1270） */
  const isFinished = curKey === "done" || curKey === "closed";

  /*
   * 進度的值。
   *
   * rPct 維持「空字串＝這次不動進度」的語意 —— submitReport 靠它判斷要不要沿用
   * 工項現有進度。所以大按鈕顯示的是「還沒選就亮工項目前的那一段」，
   * 選過之後才是使用者挑的值。
   */
  const curPct = Math.max(0, Math.min(100, Number(_.get(selTask, "progress", 0)) || 0));
  const pctValue = rPct === "" ? curPct : Math.max(0, Math.min(100, Number(rPct) || 0));
  /*
   * 送出 100% 會發生的是「送審」，不是「完成」—— 名字要照後端實際做的事取，
   * 否則下一個人會照著變數名再寫一次錯的文案。
   *
   * 後端（Web/Services/MyTaskService.cs，AddProgressRecord 與 UpdateProgress）：
   * progress >= 100 且狀態不是已完成／已關閉時，走 WorkflowService
   * TryTransitionToLegacyStatus 推到「審核中」，成功再由 ReviewService.AutoOpenReview
   * 開一張審核單並自動指派把關者（專案 PM；沒有 PM 時找系統角色「RD主管」）。
   * 真正的「已完成」發生在把關者核准的那一刻：ReviewService.Decide 自己把工項推過去，
   * 不用任何人再按一次「完成」；要求修改則退回「進行中」。
   *
   * 排除「審核中」：工項已經在審核中時，AddProgressRecord 只會留下這筆紀錄，
   * 回一句「此工項已在審核中，等待把關者決議」，不會再開第二張審核單。
   * 那種情況沒有「送出去」這件事要預告，所以不掛那面橫幅。
   */
  const pctWillReview = pctValue >= 100 && !isFinished && curKey !== "review";
  const pctWillStart = pctValue > 0 && pctValue < 100 && curKey === "pending";
  /*
   * 待處理 / 進行中這兩個狀態，進度本身就會把狀態推走（後端 MyTaskService：
   * 進度 >0 從待處理自動轉進行中、進度 >= 100 自動送審轉成審核中），操作的人
   * 不需要再去動收合區裡的「改成哪一個狀態」。這件事沒講的話，多數人會先改狀態
   * 再填進度，或是改了狀態又選 100，白繞一圈。
   */
  const pctDrivesStatus = curKey === "pending" || curKey === "progress";

  /* 工項本來就已經是「已完成」時仍要顯示目前狀態，否則下拉會是空的 */
  const statusOpts = useMemo(
    () => (STATUS_OPTS.includes(curLabel) ? STATUS_OPTS : [curLabel, ...STATUS_OPTS]),
    [curLabel]
  );

  /*
   * 選取的工項一定要是「目前分頁看得到的那幾筆」之一。
   *
   * selTask 是從全部工項找的，不是只找目前分頁，所以光靠「selId 是 null 才自動選」
   * 會有兩個誤導：分頁切到空分類時，右欄繼續顯示上一個分頁選的那筆；
   * 分頁有東西但選的是別頁的工項時，右欄顯示 A、左邊清單卻沒有任何一列被選起來。
   */
  useEffect(() => {
    if (mineList.length === 0) {
      if (selId != null) setSelId(null);
      return;
    }
    if (selId == null || !mineList.some((t) => t.id === selId)) setSelId(mineList[0].id);
  }, [mineList, selId]);

  /*
   * 換一件工項就把整張回報表清空。
   *
   * 改版前是不清的 —— 打到一半去點別的列，那段字會原封不動留在框裡，按下送出
   * 就寫到另一件工項的歷程上，而且完全看不出來。
   * 草稿留著的價值遠小於「回報寫到別人身上」的代價。
   */
  useEffect(() => {
    setRPct("");
    setRText("");
    setRHtml("");
    setRichMode(false);
    setTextErr("");
    setRGit("");
    setRFile(null);
    setRStatus("");
    setRResId("");
    setMoreOpen(false);
    setLogsOpen(false);
    setCmtOpen(false);
  }, [selId]);

  /* ---------- 驗收條件與審核單 ---------- */

  const loadReview = useCallback(async (taskId) => {
    if (!taskId) {
      setDod([]);
      setReviews([]);
      setApprovalEffective(null);
      return;
    }
    try {
      const res = await apiGet("/Review/List", { taskId });
      setDod(_.get(res, "criteria", []) || []);
      setReviews(_.get(res, "reviews", []) || []);
      setApprovalEffective(_.get(res, "approval_effective", null));
    } catch (e) {
      // 審核模組可能尚未就緒，維持空清單即可（不可讓整頁白畫面）
      setDod([]);
      setReviews([]);
      setApprovalEffective(null);
    }
  }, []);

  const loadLogs = useCallback(async (taskId) => {
    // 回報送出後才重抓的那一筆，若使用者已經改選別的工項就不抓，否則會把 B 的請求作廢、畫面卡在載入中
    if (taskId && taskId !== selIdRef.current) return;
    // 快速切換工項時先送的請求可能比較晚回來；只收最後一次的結果，免得 A 的歷程掛到 B 底下
    logsReq.current += 1;
    const seq = logsReq.current;
    if (!taskId) {
      setLogs([]);
      setLogsError(false);
      setLogsTaskId(null);
      return;
    }
    try {
      const res = await apiGet(`/MyTask/Progress/${taskId}`);
      if (seq !== logsReq.current) return;
      setLogs(_.isArray(res) ? res : _.get(res, "data_list", []) || []);
      setLogsError(false);
    } catch (e) {
      if (seq !== logsReq.current) return;
      setLogs([]);
      setLogsError(true);
    }
    setLogsTaskId(taskId);
  }, []);

  const loadComments = useCallback(async (taskId) => {
    if (!taskId) {
      setComments([]);
      return;
    }
    try {
      // 這支端點吃 ?Data=<JWT>（同工項詳情），另外帶 taskId 是給沒吃 JWT 的舊版用的
      const res = await apiGet("/Task/Comment/List", {
        Data: jwtEncode({ TaskId: Number(taskId) }),
        taskId,
      });
      setComments(Array.isArray(res) ? res : _.get(res, "data_list", []) || []);
    } catch (e) {
      setComments([]);
    }
  }, []);

  const addComment = async () => {
    const t = commentDraft.trim();
    if (!t || !selId) return;
    try {
      await apiPostForm("/Task/Comment", {
        Data: jwtEncode({ TaskId: Number(selId), Content: t }),
        taskId: selId,
        content: t,
      });
      setCommentDraft("");
      loadComments(selId);
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  /* 不跳確認：一則留言重打的成本很低，而且刪了在工項詳情的活動紀錄看得出來 */
  const removeComment = async (cm) => {
    try {
      await apiDelete("/Task/Comment", { Data: jwtEncode({ CommentId: Number(cm.id) }) });
      setComments((s) => s.filter((x) => x.id !== cm.id));
      toast("已刪除留言");
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  /* ---------- 附件（PM 傳上來的規格書 / 設計稿，RD 在這頁直接拿） ---------- */

  const loadAtts = useCallback(async (taskId) => {
    if (!taskId) {
      setAtts([]);
      return;
    }
    try {
      const res = await apiGet(`/Task/Attachment/List/${taskId}`);
      setAtts(_.isArray(res) ? res : _.get(res, "data_list", []) || []);
    } catch (e) {
      // 沒有工項模組權限時取不到，維持空清單就好（不要讓整頁掛掉）
      setAtts([]);
    }
  }, []);

  /**
   * 下載附件 —— /Task/Attachment/Download/{id} 有 [Authorize]，
   * 直接開 <a href> 不會帶 Bearer token（必定 401），所以取 blob 再存檔。
   */
  const downloadAtt = async (a) => {
    try {
      const blob = await request.get(`/Task/Attachment/Download/${a.id}`, null, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.file_name || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  /**
   * 下載進度回報夾帶的檔案。端點一樣有 [Authorize]，一樣得取 blob 再存檔。
   */
  const downloadProgressFile = async (l) => {
    try {
      const blob = await request.get(`/MyTask/Progress/Download/${l.id}`, null, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = l.fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  useEffect(() => {
    loadReview(selId);
    loadLogs(selId);
    loadAtts(selId);
    loadComments(selId);
  }, [selId, loadReview, loadLogs, loadAtts, loadComments]);

  /* 解決結論選項全系統共用，載一次就好 */
  useEffect(() => {
    apiGet("/Workflow/Resolutions")
      .then((r) => setResolutions(Array.isArray(r) ? r : []))
      .catch(() => setResolutions([]));
  }, []);

  /*
   * 選了新狀態就先問後端「這條轉換要不要填結論」，要的話才把選單露出來。
   * 不用狀態名稱硬判（工作流是可以改的），也不要一律顯示 ——
   * 那會讓每次改狀態都多一個沒必要的欄位。
   */
  useEffect(() => {
    let alive = true;
    const target = rStatus && rStatus !== curLabel ? LABEL_TO_STATUS[rStatus] : null;
    if (!selId || !target) {
      setNeedRes(false);
      return undefined;
    }
    findTransition(selId, target)
      .then((hit) => {
        if (alive) setNeedRes(!!hit.requires_resolution);
      })
      .catch(() => {
        // 查不到轉換不是這裡該報的錯，送出時會帶著原因跳出來
        if (alive) setNeedRes(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selId, rStatus, curLabel]);

  /* 預設停在第一個（後端依 sort_order 排，第一個就是最常用的） */
  useEffect(() => {
    if (needRes && !rResId && resolutions.length > 0) setRResId(String(resolutions[0].id));
  }, [needRes, rResId, resolutions]);

  const toggleDod = async (c, i) => {
    const next = !(c.is_checked || c.isChecked);
    // 先樂觀更新，避免等待往返時按了沒有反應
    setDod((s) => s.map((x, ix) => (ix === i ? { ...x, is_checked: next } : x)));
    try {
      // 後端 ReviewController.ToggleCriterion 是 [FromBody] ReqToggleCriterion{Id,IsChecked}，只吃 JSON
      await apiPutJson("/Review/Criterion", { id: c.id, isChecked: next });
      loadReview(selId);
    } catch (e) {
      setDod((s) => s.map((x, ix) => (ix === i ? { ...x, is_checked: !next } : x)));
      toast(errMsg(e), "error");
    }
  };

  const dodDone = dod.filter((c) => c.is_checked || c.isChecked).length;

  /* ---------- 狀態推進 ---------- */

  /**
   * 後端 /Workflow/Transition 只認 transitionId（ReqTransition 沒有 toStatus 欄位），
   * 一律先問 /Workflow/Transitions 換算；查不到對應轉換或守門擋下時，把原因原樣丟出來讓上層 toast。
   */
  const findTransition = async (taskId, toKey) => {
    const code = STATUS_CODE[toKey];
    let list = [];
    try {
      const res = await apiGet("/Workflow/Transitions", { taskId });
      list = _.get(res, "transitions", []) || [];
    } catch (e) {
      // 工作流表未就緒時後端會回明確訊息（例：請先套用 V012 migration），直接呈現給使用者
      throw new Error(errMsg(e));
    }
    const hit = list.find((x) => {
      const legacy = _.get(x, "to_status.legacy_status");
      return legacy != null && Number(legacy) === code;
    });
    if (!hit) throw new Error(`目前狀態無法直接轉為「${STATUS_LABEL[toKey]}」`);
    return hit;
  };

  const doTransition = async (taskId, toKey, resolutionId = null) => {
    const hit = await findTransition(taskId, toKey);
    if (hit.allowed === false) {
      const reasons = _.get(hit, "blocked_reasons", []) || [];
      throw new Error(reasons.join("；") || "目前不允許此狀態轉換");
    }
    // 需要結論的轉換沒帶 resolutionId 就是必定失敗，後端也擋得住，這裡先講清楚
    if (hit.requires_resolution && !resolutionId) throw new Error("需選擇解決結論");
    await apiPostJson("/Workflow/Transition", {
      taskId,
      transitionId: hit.id,
      resolutionId: hit.requires_resolution ? Number(resolutionId) : null,
    });
  };

  /**
   * 一鍵推到下一站（不寫回報）。
   *
   * 改版前這顆鈕在清單的每一列上，但新版的列整列就是一顆按鈕（整列可點、
   * 遠大於 44px），裡面不能再塞按鈕。所以它搬到右欄的收合區、只作用在選取的那一件 ——
   * 能力一樣在，只是要先選一件事，而那本來就是使用者的第一個動作。
   */
  /** target 不給就走 NEXT_MAP；只有 approvedButStuck 那顆會指定 "done" */
  const advance = async (t, target) => {
    const nxt = target || NEXT_MAP[t.status];
    try {
      /*
       * 需要結論的轉換不能就這樣按下去。與其丟一句「需選擇解決結論」讓人卡住，
       * 直接把狀態下拉停在目標狀態並把收合區打開 —— 結論選單就在那裡，
       * 選完按送出回報即可。
       */
      const hit = await findTransition(t.id, nxt);
      /*
       * 「審核已通過，標記為已完成」遇到要結論的轉換：不能照下面那條把下拉停在目標狀態 ——
       * 下拉裡沒有「已完成」，畫面會顯示成第一項「待處理」，還要先寫一句回報才送得出去。
       * 結論照後端自動完成的預設挑（ReviewService.DefaultResolutionId）：代碼 done 優先，沒有才取第一個。
       */
      if (hit.requires_resolution && nxt === "done") {
        const res =
          resolutions.find((x) => _.get(x, "key") === "done") || resolutions[0] || null;
        if (!res) {
          toast("「已完成」須選擇解決結論，但目前無法取得結論清單，請至工項頁的「⋯ 更多」操作", "error");
          return;
        }
        await doTransition(t.id, nxt, res.id);
        toast(`已將「${t.name}」設為${STATUS[nxt].label}`);
        reload();
        if (t.id === selId) {
          loadReview(selId);
          loadLogs(selId);
        }
        return;
      }
      if (hit.requires_resolution) {
        setRStatus(STATUS_LABEL[nxt]);
        setMoreOpen(true);
        toast(`「${STATUS[nxt].label}」須選擇解決結論，請於下方選擇後再送出`, "error");
        return;
      }
      await doTransition(t.id, nxt);
      toast(`已將「${t.name}」設為${STATUS[nxt].label}`);
      reload();
      if (t.id === selId) {
        loadReview(selId);
        loadLogs(selId);
      }
    } catch (e) {
      toast(errMsg(e), "error");
    }
  };

  /* ---------- 送出回報 ---------- */

  // 不需要 projectId：抽屜靠 taskId 自己載內容，沒有專案歸屬的工項一樣打得開
  const openDetail = (t) => {
    if (!t) return;
    setDrawerTask(t);
  };

  const submitReport = async () => {
    if (!selTask || submitting.current) return;
    /*
     * 送 HTML，不要送 innerText。
     *
     * 完整編輯器模式要連圖一起送：原本這裡取的是 innerText，使用者貼的圖在送出前
     * 就沒了，而且怎麼查都查不到，因為後端根本沒收到。
     * 一般輸入格模式則把純文字跳脫成 HTML（textToHtml），換行才留得住，
     * 而且與舊資料同一種格式，歷程那邊用同一個 RichView 就顯示得出來。
     */
    const edEl = richMode ? document.getElementById("rep-editor") : null;
    const html = richMode ? (edEl ? edEl.innerHTML : rHtml) || "" : textToHtml(rText);
    const d = toPlainText(html);
    const hasBody = !!d || /<img/i.test(html);
    /*
     * 「這次做了什麼」是必填（設計稿 L1200）。少了這句話的回報，主管在日報上
     * 看到的就是一個沒頭沒尾的百分比。
     * 只想改狀態、不想寫字的人仍有路：收合區裡那顆「直接{下一站}」走的是工作流
     * 轉換，本來就不寫進度紀錄。
     */
    if (!hasBody) {
      setTextErr("請填寫「本次工作內容」，此內容將顯示於主管的日報。");
      return;
    }
    const stChanged = !!rStatus && rStatus !== curLabel;
    /*
     * 已知會被守門擋下就不要先寫進度記錄。
     * 不擋的話歷程上會多一筆「狀態更新為「已關閉」」，但狀態其實沒變 ——
     * 記錄說了謊，而且按幾次就多幾筆。
     */
    if (stChanged && needRes && !rResId) {
      toast("此狀態須選擇解決結論", "error");
      setMoreOpen(true);
      return;
    }

    // 狀態有異動時，回報內容尾端補上「狀態更新為「X」」，狀態變更才會留在進度歷程裡
    const tail = `狀態更新為「${rStatus}」`;
    const content = stChanged ? `${html}<p>${tail}</p>` : html;
    // 單一輸入框同時容納分支與 commit，以第一個空白／# 切開送給後端的兩個欄位
    const gitParts = String(rGit).trim().split(/[\s#]+/).filter(Boolean);
    // progress 是後端必填 int，且 AddProgressRecord 會無條件覆寫工項進度：
    // 這次沒動進度就沿用現有值，動了就夾在 0..100，避免把進度洗成 0。
    // 選「做完了」等於「我做完了，送審」：上面那面橫幅講的就是這件事，這裡不另外攔截。
    const pctRaw = String(rPct).trim();
    const pctNum = /^\d+(\.\d+)?$/.test(pctRaw) ? Math.round(Number(pctRaw)) : null;
    const pctToSend = pctNum == null ? Number(selTask.progress) || 0 : Math.max(0, Math.min(100, pctNum));

    submitting.current = true;
    try {
      // 夾檔是選填。後端 AddProgressRecord 一直收得下 file
      const fd = new FormData();
      fd.append("taskId", selTask.id);
      fd.append("content", content || "");
      fd.append("progress", pctToSend);
      fd.append("gitBranch", gitParts[0] || "");
      fd.append("gitCommit", gitParts.slice(1).join(" ") || "");
      if (rFile) fd.append("file", rFile);
      const res = await apiPostForm("/MyTask/Progress", fd);
      // 紀錄已寫入就先清空輸入，狀態推進若被守門擋下也不會在重送時寫出第二筆重複歷程
      setRPct("");
      setRText("");
      setRHtml("");
      setTextErr("");
      setRGit("");
      setRFile(null);
      if (edEl) edEl.innerHTML = "";

      if (stChanged) await doTransition(selTask.id, LABEL_TO_STATUS[rStatus], rResId || null);
      setRStatus("");
      setRResId("");
      /*
       * 後端回的 data 是 { id, status, message }（MyTaskController.AddProgressRecord
       * 的註解也寫明「message 說明這次順帶做了什麼，前端直接顯示」），而 utils/api.js
       * 的 dec() 交出來的就是這個 data，所以 message 拿得到。
       *
       * message 有三種：自動送審成功並列出把關者是誰、工項已在審核中、
       * 或是被工作流守門擋下的原因（最常見的是「尚未送審：尚有驗收條件未完成」）。
       * 只講「已送出回報」的話，被擋下的人會以為已經送審了，就不會回頭去勾驗收條件。
       * 擋下那一種用 error 色並拉長顯示時間，因為那句話是他現在唯一的線索。
       */
      const note = String(_.get(res, "message", "") || "").trim();
      const blocked = note.includes("尚未送審") || note.includes("狀態未變更");
      if (note) toast(`已送出回報 —— ${note}`, blocked ? "error" : undefined, 5000);
      else toast("已送出回報");
    } catch (e) {
      toast(errMsg(e), "error");
    } finally {
      submitting.current = false;
      // 不論成敗都重抓：推進失敗時進度歷程已經多一筆，畫面要跟得上
      reload();
      loadLogs(selTask.id);
      loadReview(selTask.id);
      // 頁首的提醒與底部「今天的紀錄」的筆數都要跟著這次送出改變
      setDayTick((n) => n + 1);
    }
  };

  /* ===================== view model ===================== */

  /*
   * 需求說明是工項詳情的 RichEditor 寫入的 HTML。
   * 照 HTML 呈現而不是剝成純文字：PM 在說明裡貼的截圖與排版就是一半的資訊。
   */
  const reportDescHtml = String((selTask && selTask.desc) || "");
  const hasReportDesc = !!toPlainText(reportDescHtml) || /<img/i.test(reportDescHtml);

  // 選到的工項歷程還沒回來就先不下結論：沿用上一筆工項的歷程、或閃一下空狀態都會誤導
  const logsLoading = selId != null && logsTaskId !== selId;

  /*
   * 回報內容存的是 HTML（可能含圖），所以拆成兩塊：
   * body 交給 RichView 照 HTML 呈現，進度／git 這些系統欄位另外用純文字接在後面。
   * 舊資料是純文字，RichView 一樣顯示得出來，不必轉檔。
   */
  const reportLog = useMemo(
    () =>
      logs.map((l, i) => {
        const body = String(l.content || "").trim();
        const pv = l.progress != null && l.progress !== "" ? Number(l.progress) : null;
        /*
         * 「進度 40% → 50%」的前半段沒有欄位可以拿，但推得出來：後端是
         * OrderByDescending(CreateAt)，所以陣列裡後面那一筆就是上一次回報。
         * 推不出來（最早那一筆）就只寫現在的值，不要編一個 0%。
         */
        let prev = null;
        for (let j = i + 1; j < logs.length; j += 1) {
          const q = logs[j];
          const qv = q.progress != null && q.progress !== "" ? Number(q.progress) : null;
          if (qv != null && !Number.isNaN(qv)) {
            prev = qv;
            break;
          }
        }
        const meta = [];
        if (pv != null && !Number.isNaN(pv)) {
          meta.push(prev != null && prev !== pv ? `進度 ${prev}% → ${pv}%` : `進度 ${pv}%`);
        }
        const git = [l.git_branch || l.gitBranch, l.git_commit || l.gitCommit].filter(Boolean).join(" ");
        if (git) meta.push(git);
        return {
          body,
          empty: !body && meta.length === 0,
          meta: meta.join("，"),
          at: l.create_at || l.createAt,
          time: fmtDateTime(l.create_at || l.createAt),
          /* 事後依 commit 補登的歷史記錄（不計入績效），畫面要看得出來不是當時親手回報的 */
          backfill: !!(l.is_backfill ?? l.isBackfill),
          /* 回報時可以夾一張截圖／檔案，後端一直存得下也下載得到 */
          id: l.id,
          fileName: l.file_name || l.fileName || "",
          hasFile: !!(l.has_file ?? l.hasFile ?? (l.file_name || l.fileName)),
        };
      }),
    [logs]
  );

  /* 今天寫的那幾筆（底部那張唯讀的日報卡）。補登的不算 —— 那不是今天有人真的回報 */
  const todayLog = useMemo(
    () => reportLog.filter((l) => !l.backfill && isSameDay(l.at, today)),
    [reportLog, today]
  );
  /** 今天總共回報了幾筆（跨所有工項）。這個數字只有 /MyTask/Today 算得出來 */
  const todayReported = Number(_.get(dayStat, "progress_count", 0)) || 0;

  /*
   * 頁首的日報提醒（設計稿 L1098-L1103）。
   *
   * 只在「上班日 ＋ 今天完全沒回報過」時出現，六日與國定假日完全不提醒
   * （is_workday 由後端依假日表判定，與個人績效的漏填偵測同一套規則）。
   *
   * 另外排除會看到全站橫幅的角色：layouts/parts/DailyTaskBanner.js 已經對
   * RD／RD主管 在每一頁最上方掛了一條同樣意思的紅色提示條，
   * 這裡再畫一條就是同一句話說兩次、佔掉兩倍版面。
   * 反過來說，不在那份名單上的人（PM、主管）手上一樣有工項，
   * 這條就是他們唯一會看到的提醒。
   */
  const showDailyHint =
    !!dayStat &&
    dayStat.is_workday === true &&
    dayStat.has_task === false &&
    !isReportingRole(_.get(userInfo, "role"));

  /**
   * 做完的工項是誰審的。
   *
   * 取最後一張有結論的審核單上的把關者。/Review/List 回的是帳號（reviewer_id）
   * 不是姓名，而這一頁沒有成員名冊可以換算 —— 寧可顯示帳號，也不要編一個名字，
   * 更不要因為換不到名字就整句不講（使用者問的就是「誰審的」）。
   */
  const closedReview = useMemo(() => (reviews || []).find((r) => _.get(r, "closed_at")) || null, [reviews]);
  const reviewedBy = useMemo(() => {
    if (!closedReview) return "";
    return (_.get(closedReview, "assignees", []) || [])
      .map((a) => _.get(a, "reviewer_id"))
      .filter(Boolean)
      .join("、");
  }, [closedReview]);

  /*
   * 審核已經通過、工項卻還停在「審核中」。
   *
   * 正常流程不會出現這種狀態 —— 通過的那一刻 ReviewService.Decide 就自動完成了。
   * 只有兩種來源：自動完成上線之前就核准、當時還得有人再按一次「完成」的舊資料；
   * 或自動轉換被別的守門擋下（最常見的是還有驗收條件沒勾）。
   * 只有這時候才給「標成已完成」那顆鈕（NEXT_MAP 已經拿掉 review → done），
   * 否則它不是按了必定失敗，就是讓人以為核准之後還要回來按一次。
   * reviews[0] 是最新一張（後端由新到舊給）。
   */
  const approvedButStuck =
    curKey === "review" &&
    reviewStatusKey(_.get(reviews, [0, "status"])) === "approved" &&
    // 通過之後重新打開過的，那張通過不算數（後端 ApprovalBlockReason）—— 按了也會被擋，不能叫人去按
    approvalEffective !== false;

  /*
   * 審核人已經要求修改、工項卻還停在「審核中」。
   *
   * 要求修改的那一刻後端會自動退回「進行中」；會停在這裡的是自動退回上線前的舊資料，
   * 或那次轉換被工作流擋下。這時沒有待審的審核單、沒有人會再決議，不給出口就永遠卡著。
   * 出口兩個：改好了回報 100%（後端 AddProgressRecord 會補開一張審核單），
   * 或還要改一陣子就先改回「進行中」。
   */
  const latestReviewKey = reviewStatusKey(_.get(reviews, [0, "status"]));
  const rejectedButStuck =
    curKey === "review" && (latestReviewKey === "rejected" || latestReviewKey === "returned");

  /** 一眼區那一行「你負責 · 今天 9/21 要交 · 新頭家網」（缺哪一段就不寫哪一段） */
  const factLine = selTask
    ? [
        "由您負責",
        selTask.dueRaw ? `${dueLabel(selTask.dueRaw, today)}到期` : "",
        selTask.projectName || "",
        selTask.code || "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  /** 左欄的一列 */
  const rowOf = (t) => {
    const tag = tagOf(t, today);
    const finished = t.status === "done" || t.status === "closed";
    const typeLabel = typeLabelOf(t.raw);
    const metas = (finished
      ? [
          t.projectName || "",
          t.completedAt ? `${dueLabel(t.completedAt, today)}完成` : "",
          t.code || "",
        ]
      : [
          t.projectName || "",
          t.dueRaw ? `${dueLabel(t.dueRaw, today)}到期` : "",
          /* 優先級原本是一顆膠囊。新版的列只放一顆徽章（一列只講一件事），
             所以緊急與高改成寫在下面那行字裡；一般與低本來就不必特別講 */
          t.prio === "urgent" || t.prio === "high" ? PRIO_LABEL[t.prio] : "",
          t.code || "",
        ]
    ).filter(Boolean);
    return (
      <RowButton
        key={t.id != null ? t.id : t.code}
        data-tour="my-row"
        aria-current={t.id === selId ? "true" : undefined}
        selected={t.id === selId}
        tone={tag.kind === "attention" ? "attention" : ""}
        onClick={() => setSelId(t.id)}
        side={
          <StatusChip kind={tag.kind} mark={tag.mark}>
            {tag.text}
          </StatusChip>
        }
      >
        <Box sx={{ flex: "1 1 auto", minWidth: 0, display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
            {typeLabel ? <StatusChip kind="type">{typeLabel}</StatusChip> : null}
            <Typography variant="subtitle1" component="span">
              {t.name || "—"}
            </Typography>
          </Stack>
          {metas.length > 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
            >
              {metas.map((m) => (
                <span key={`${t.id}-${m}`}>{m}</span>
              ))}
            </Typography>
          ) : null}
        </Box>
      </RowButton>
    );
  };

  /* 沒有內容的收合區整個不畫（硬規則 2）；末尾那一排「＋」就是它們的入口 */
  const showDod = dod.length > 0;
  const showLogs = logsLoading || logsError || reportLog.length > 0;
  const showAtts = atts.length > 0;
  const showCmt = comments.length > 0 || cmtOpen;

  return (
    <>
      {/*
        今天完全沒回報過才出現。

        設計稿右邊那顆「補昨天的（9/20）」沒有畫：補填昨天的紀錄要能指定日期送出，
        而 POST /MyTask/Progress 的參數只有 taskId / content / progress / gitBranch /
        gitCommit / file（Web/Controllers/MyTaskController.cs 的 AddProgressRecord），
        PUT 那支也一樣 —— 後端沒有任何地方收得下「這筆算在哪一天」，紀錄的日期一律
        是寫入當下的 create_at。畫一顆按下去只會寫成今天的鈕，比沒有更糟。
        後端補上日期參數之後，照設計稿用 Banner 的 action 加回來即可。
      */}
      {showDailyHint && (
        <Banner kind="warn" role="status" icon={<MdErrorOutline />}>
          <strong>今日尚無紀錄　</strong>
          於下方任一工項按「送出回報」，系統即會自動產生今日紀錄（日報），無須另行填寫。
        </Banner>
      )}

      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 3 }}>
        <Typography variant="h1" component="h1">我的工項</Typography>
        <Typography variant="body2" color="text.secondary" component="span">
          {[meName, todayTitle(today)].filter(Boolean).join(" · ")}
        </Typography>
      </Stack>

      {/*
        摘要列：首頁合併後取代原本「今天」頁的三張卡。
        待我審核 → 捲到下面的「待我審核」區；今日到期／已逾期 → 篩選下面的清單（再按一次取消）。
      */}
      <Box
        role="group"
        aria-label="今日摘要"
        sx={{ mt: 2, display: "grid", gap: 1.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, maxWidth: 840 }}
      >
        {[
          {
            key: "review",
            label: "待我審核",
            n: reviewRows.length,
            tone: "primary",
            hint: reviewRows.length > 0 ? "查看" : "前往審核清單",
            /* 有待審的就捲到下面那一區；沒有就直接去審核清單（那裡還看得到我送出的） */
            onClick: () => {
              const el = document.getElementById("mt-review");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              else navigate("/Approvals");
            },
          },
          { key: "due", label: "今日到期", n: dueCount, tone: "warning", onClick: () => setQuick((q) => (q === "due" ? "" : "due")), pressed: quick === "due" },
          { key: "late", label: "已逾期", n: lateCount, tone: "error", onClick: () => setQuick((q) => (q === "late" ? "" : "late")), pressed: quick === "late" },
        ].map((c) => (
          <ButtonBase
            key={c.key}
            onClick={() => {
              c.onClick();
              setShowAll(false);
            }}
            aria-pressed={c.pressed === undefined ? undefined : c.pressed}
            sx={{
              justifyContent: "space-between",
              gap: 2,
              px: 2.5,
              py: 1.75,
              textAlign: "left",
              borderRadius: "var(--radius-md)",
              border: 1,
              borderColor: c.pressed ? `${c.tone}.main` : "divider",
              bgcolor: c.pressed ? `${c.tone}.light` : "background.paper",
              "&:hover": { borderColor: `${c.tone}.main` },
              "&.Mui-focusVisible": { outline: "var(--focus-w) solid var(--focus-ring)", outlineOffset: 2 },
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {c.label}
              </Typography>
              <Typography variant="h2" component="p" sx={{ color: c.n > 0 ? `${c.tone}.main` : "text.primary" }}>
                {c.n} 件
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              {c.hint || (c.pressed ? "取消篩選" : "篩選清單")}
            </Typography>
          </ButtonBase>
        ))}
      </Box>

      {/*
        待我審核：別人送給我審的工項，跟「我負責的工項」是兩件事，所以獨立一區、不放進分頁。
        一列一件，點下去進審核頁（/Approvals/{taskId}）做「通過／要求修改」。
        列的內容與審核清單頁（pages/Approval/Index.js 的 buildToMeRows）同一份。
        沒有待審項目時整區不畫；摘要列的「0 件」已經講了這件事。
      */}
      {reviewRows.length > 0 && (
        <Box component="section" id="mt-review" aria-labelledby="mt-review-h" sx={{ mt: 3, scrollMarginTop: 80 }}>
          <Stack direction="row" spacing={1.5} useFlexGap sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
            <Typography variant="h2" component="h2" id="mt-review-h">
              待我審核
            </Typography>
            <Typography variant="body2" color="text.secondary" component="span">
              {`${reviewRows.length} 件`}
            </Typography>
          </Stack>
          <Card sx={{ mt: 1.5 }}>
            {reviewRows.slice(0, REVIEW_MAX).map((r) =>
                r.taskId != null ? (
                  <RowButton
                    key={r.key}
                    tone={r.attention ? "attention" : ""}
                    onClick={() => navigate(`/Approvals/${r.taskId}`)}
                    title={r.title}
                    meta={[r.type, ...r.metas].filter(Boolean).map((m) => (
                      <span key={m}>{m}</span>
                    ))}
                    side={
                      r.tag ? (
                        <StatusChip kind={r.tag.tone} mark={r.tag.mark} size="small">
                          {r.tag.text}
                        </StatusChip>
                      ) : null
                    }
                  />
                ) : (
                  <DataRow key={r.key} title={r.title} meta={r.metas.join(" · ")} />
                )
              )}
            <Box sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: "divider" }}>
              <Button variant="text" onClick={() => navigate("/Approvals")}>
                {reviewRows.length > REVIEW_MAX ? `查看全部 ${reviewRows.length} 件審核` : "前往審核清單（含我送出的審核）"}
              </Button>
            </Box>
          </Card>
        </Box>
      )}

      <Typography variant="h2" component="h2" sx={{ mt: 4 }}>
        我負責的工項
      </Typography>

      {/*
        MUI Tabs 自帶 role="tablist" / role="tab" 與 aria-selected，
        輔助科技讀得出哪一個被選中。作法與 pages/Approval/Index.js 一致。
      */}
      <Tabs
        value={curTab}
        onChange={(_e, k) => {
          setMyTab(k);
          setQuick("");
          setShowAll(false);
        }}
        aria-label="我的工項"
        data-tour="my-tabs"
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ mt: 2 }}
      >
        {TABS.map(([k, label]) => {
          const n = countOf(k);
          return (
            /* 件數 0 就不顯示數字（設計稿 L1114）：一排 0 只是在告訴人「這裡沒東西」，
               而那件事不需要一個數字來講 */
            <Tab key={k} value={k} aria-controls="mt-list" label={`${label} ${n > 0 ? n : ""}`} />
          );
        })}
      </Tabs>
      {quick ? (
        <Banner
          kind="info"
          sx={{ mt: 2 }}
          action={
            <Button variant="text" color="inherit" onClick={() => setQuick("")}>
              清除篩選
            </Button>
          }
        >
          {`目前只顯示「${quick === "due" ? "今日到期" : "已逾期"}」的工項（${mineList.length} 件，含審核中）。`}
        </Banner>
      ) : null}

      {/*
        這個分頁沒有工項時只畫一張卡：左右兩欄是「選一件 → 回報這一件」，
        一件都沒有就沒有東西可選，硬分兩欄只會變成兩個並排的空框。
      */}
      {mineList.length === 0 ? (
        <Card id="mt-list" role="tabpanel" sx={{ mt: 2 }}>
          <EmptyState
            title={quick ? "沒有符合篩選條件的工項" : curTab === "done" ? "尚無已完成的工項" : curTab === "waiting" ? "目前沒有等待審核的工項" : "目前沒有待辦工項"}
            text={
              quick
                ? "請清除篩選以檢視全部工項。"
                : curTab === "todo"
                  ? "指派給您或由您建立的工項將顯示於此。"
                  : "請切換上方分頁檢視其他工項。"
            }
            action={
              curTab === "todo" && !quick ? (
                <Button variant="outlined" startIcon={<MdAdd aria-hidden="true" />} onClick={() => navigate("/Task/New")}>
                  新增工項
                </Button>
              ) : null
            }
          />
        </Card>
      ) : (
      /* 左右兩欄；大字模式與窄螢幕改成單欄 */
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: "1fr",
          "@media (min-width: 1101px)": { gridTemplateColumns: "minmax(300px, 400px) 1fr" },
          gap: 3,
          alignItems: "start",
          '[data-textsize="lg"] &': { gridTemplateColumns: "1fr" },
        }}
      >
        {/* ---------- 左：我的工項，整列可點 ---------- */}
        <Card id="mt-list" role="tabpanel">
          {visibleList.map(rowOf)}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1,
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            {/*
              改版前這裡是「‹ 上一頁 / 下一頁 ›」的分頁器（一頁 6 筆）。
              分頁器要先知道自己在第幾頁才知道該按哪一邊，而這份清單只是「我手上的事」，
              一次攤開才看得完 —— 所以改成先給 8 列、其餘一顆鈕展開，一件都沒有藏起來。
            */}
            {hiddenCount > 0 && (
              <Button variant="text" type="button" onClick={() => setShowAll(true)}>
                顯示更多（{hiddenCount}）
              </Button>
            )}
            <Button
              variant="text"
              type="button"
              startIcon={<MdAdd aria-hidden="true" />}
              onClick={() => navigate("/Task/New")}
            >
              新增工項
            </Button>
          </Box>
        </Card>

        {/* ---------- 右：這一件 ---------- */}
        <Box sx={{ minWidth: 0 }}>
          {!selTask && (
            <Card component="section">
              <Box sx={{ textAlign: "center", py: 8, px: 3 }}>
                <Typography variant="h2" component="p">
                  尚未選擇工項
                </Typography>
                <Typography sx={{ mt: 1 }}>
                  請從左側選擇工項，即可於此回報進度。
                </Typography>
              </Box>
            </Card>
          )}

          {selTask && (
            <>
              {/* ---- 一眼區（設計稿 L1160-L1186） ---- */}
              <Card component="section">
                <CardContent>
                  <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                    {typeLabelOf(selTask.raw) ? <StatusChip kind="type">{typeLabelOf(selTask.raw)}</StatusChip> : null}
                    {(() => {
                      const tag = tagOf(selTask, today);
                      return (
                        <StatusChip kind={tag.kind} mark={tag.mark}>
                          {tag.text}
                        </StatusChip>
                      );
                    })()}
                    {/* 緊急與高才貼，一般與低不必占一格 */}
                    {(selTask.prio === "urgent" || selTask.prio === "high") && (
                      <StatusChip kind="warn" mark={<MdErrorOutline />}>
                        {PRIO_LABEL[selTask.prio]}
                      </StatusChip>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button variant="text" type="button" data-tour="my-view" onClick={() => openDetail(selTask)}>
                      檢視完整工項
                    </Button>
                  </Stack>

                  <Typography variant="h2" component="h2" sx={{ mt: 1.5 }}>
                    {selTask.name || "—"}
                  </Typography>
                  <Typography sx={{ mt: 1 }}>{factLine}</Typography>

                  {/* 做完的工項不再顯示進度條（做完的只顯示結果）；逾期的才換成紅色 */}
                  {!isFinished && (
                    <Progress
                      value={curPct}
                      tone={isLate(selTask, today) ? "attention" : "progress"}
                      ariaLabel={`進度 ${curPct}%`}
                      sx={{ mt: 2 }}
                    />
                  )}

                  {/*
                    需求說明。設計稿寫的是「{開單人}交代的」，但 /MyTask/List 的投影裡
                    沒有開單人（見 MyTaskService.GetMyTasks 的 Select），這一頁也沒有別的
                    地方拿得到那個名字 —— 所以只寫「交代的事」，不編一個人名。
                    沒有需求說明時整條不畫（硬規則 2），不再留一句「（此工項尚無需求說明）」。
                  */}
                  {hasReportDesc && (
                    <Banner kind="info" icon={<MdInfoOutline />} data-tour="my-desc" sx={{ mt: 2.5, "& .MuiAlert-message": { minWidth: 0 } }}>
                      {/* 需求說明是 RichView 交出來的區塊內容（段落、清單、圖片），外層要是區塊元素 */}
                      <Box sx={{ minWidth: 0 }}>
                        <strong>工項說明</strong>
                        {/* RichView 會夾住圖寬（截圖才不會撐爆右欄），點圖還能放大看清楚 */}
                        <RichView
                          html={reportDescHtml}
                          sxStyle="font-size:var(--t-body);line-height:var(--t-body-lh);color:inherit;overflow-x:auto;"
                        />
                      </Box>
                    </Banner>
                  )}
                </CardContent>
              </Card>

              {/* ---- 做完的工項：只顯示結果，不給整套表單（設計稿 L1259-L1270） ---- */}
              {isFinished && (
                <Card component="section" sx={{ mt: 2, borderLeft: 4, borderLeftColor: "success.main" }}>
                  <CardContent>
                    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      <Typography variant="h3" component="h2">此工項已完成</Typography>
                      {selTask.completedAt && (
                        <StatusChip kind="done" mark={<MdCheck />}>
                          {dueLabel(selTask.completedAt, today)}完成
                        </StatusChip>
                      )}
                    </Stack>
                    <Typography sx={{ mt: 1.5 }}>
                      {[
                        selTask.completedAt ? `您於 ${dueLabel(selTask.completedAt, today)}完成` : "此工項已結案",
                        reviewedBy
                          ? `${reviewedBy} 於 ${dueLabel(_.get(closedReview, "closed_at"), today)}審核`
                          : "",
                      ]
                        .filter(Boolean)
                        .join("，")}
                      。
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                      已完成的工項僅顯示結果，不提供進度、狀態與回報表單。
                      如需補充，請使用下方按鈕，內容將保留於留言與紀錄中。
                    </Typography>
                    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", mt: 2 }}>
                      <Button
                        variant="outlined"
                        type="button"
                        onClick={() => {
                          setCmtOpen(true);
                          // 收合區要先展開才有輸入框可以聚焦，所以等這一輪 render 完
                          window.setTimeout(() => cmtRef.current && cmtRef.current.focus(), 0);
                        }}
                      >
                        新增補充說明
                      </Button>
                      <Button variant="text" type="button" onClick={() => setLogsOpen(true)}>
                        檢視回報紀錄
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* ---- 回報卡（設計稿 L1188-L1220） ---- */}
              {!isFinished && (
                <Card component="section" sx={{ mt: 2 }}>
                  <CardContent>
                    <Typography variant="h3" component="h2">回報進度</Typography>

                    <Stack spacing={2} sx={{ mt: 2 }}>
                      {/*
                        進度輸入抽成了共用元件（components/design/ProgressChoice.js）：
                        工項詳情的快速回報與專案經理的代回報原本各寫一格純數字輸入，
                        跟這裡的五顆大按鈕不一樣 —— 同一件事在同一套系統裡不該有兩種填法。
                        這一版的作法（五段＋自己填）就是那個元件的內容，改以它為單一來源。

                        key 帶工項 id：換一件工項時把「自己填」的展開狀態重置掉，
                        不然看完一件 40% 的之後，下一件會莫名其妙開著輸入格。
                      */}
                      <ProgressChoice
                        key={`pct-${selId}`}
                        id="rep-pct"
                        label="目前進度"
                        help={`填寫概略值即可，之後仍可調整。目前進度為 ${curPct}%。`}
                        value={rPct}
                        currentPct={curPct}
                        onChange={setRPct}
                      />

                      <Box>
                        <Typography
                          component="label"
                          htmlFor={richMode ? "rep-editor" : "rep-text"}
                          sx={{ display: "block", fontWeight: 600, mb: 1 }}
                        >
                          本次工作內容
                          <Typography component="span" color="error" variant="body2" sx={{ ml: 1 }}>
                            必填
                          </Typography>
                        </Typography>
                        {richMode ? (
                          <RichEditor
                            id="rep-editor"
                            html={rHtml}
                            placeholder="可直接貼上圖片…"
                            minHeight={110}
                            lineHeight="1.6"
                            color="var(--ink)"
                            onChange={setRHtml}
                          />
                        ) : (
                          <TextField
                            id="rep-text"
                            multiline
                            minRows={3}
                            fullWidth
                            value={rText}
                            error={!!textErr}
                            placeholder="已新增斷線通知，尚在測試 30 分鐘未上傳的提醒…"
                            onChange={(e) => {
                              setRText(e.target.value);
                              if (textErr) setTextErr("");
                            }}
                            slotProps={{ htmlInput: { "aria-describedby": "rep-text-help" } }}
                          />
                        )}
                        <FormHelperText id="rep-text-help">
                          簡述即可，例如：已新增斷線通知，尚在測試。內容清楚即可，無須修飾。
                        </FormHelperText>
                        {textErr && (
                          <FormHelperText error sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <MdWarningAmber aria-hidden="true" />
                            {textErr}
                          </FormHelperText>
                        )}
                        {/*
                          改版前這一格是富文本編輯器（工具列有「＋圖片」，貼得了截圖）。
                          設計稿把工具列拿掉了，但「貼圖」是既有能力，不能跟著工具列一起消失，
                          所以收成一顆文字按鈕：平常是一個乾淨的輸入格，要貼圖才換過去。
                        */}
                        <Box sx={{ mt: 1 }}>
                          <Button
                            variant="text"
                            type="button"
                            onClick={() => {
                              if (richMode) {
                                // 換回來裝不下圖（純文字沒有圖片這種東西），所以按鈕上就要先講
                                setRText(toPlainText(rHtml));
                                setRichMode(false);
                              } else {
                                setRHtml(textToHtml(rText));
                                setRichMode(true);
                              }
                            }}
                          >
                            {richMode ? "切換為純文字輸入（將移除已貼上的圖片）" : "切換為完整編輯器（可貼上圖片）"}
                          </Button>
                        </Box>
                      </Box>

                      {/*
                        選「做完了」是「送出去就交出去了」的重要後果，依硬規則 4 用 Banner，
                        不寫成灰字小字。

                        設計稿那句指名了審核人是誰，這裡不指名：把關者是後端
                        ReviewService.PickReviewers 在送出當下才挑的（專案 PM，沒有 PM
                        才找系統角色「RD主管」），前端手上沒有這個人名，寫死會說謊。
                      */}
                      {/*
                        審核通過就自動完成、要求修改就自動退回進行中（ReviewService.Decide），
                        所以橫幅要把「送出去之後」一路講到底：不用回來再按一次完成。
                        這段說法與 components/task/TaskWork.js 的橫幅、resources/tours.js 的
                        my-submit 是同一套，三處要一起改。
                      */}
                      {pctWillReview ? (
                        <Banner kind="warn" icon={<MdErrorOutline />}>
                          進度選擇 100% 時，工項將<strong>自動送交審核人</strong>
                          （專案 PM；無 PM 時為 RD 主管）審核，送審後無法再編輯。
                          審核通過後自動變更為「已完成」，無須另行操作；要求修改時退回「進行中」，修改完成後請重新送審。
                          若驗收條件尚未全部勾選，送審將被拒絕，送出後會顯示原因。
                        </Banner>
                      ) : rejectedButStuck ? (
                        <Banner kind="attention" icon={<MdWarningAmber />}>
                          審核人已要求修改，但工項仍停留在「審核中」，不會再有人進行決議。
                          修改完成後請回報 100% 以重新送審；如需較長時間修改，請展開下方「變更狀態與工時」，按「退回進行中」。
                        </Banner>
                      ) : approvedButStuck ? (
                        /* 還沒收尾的事，不能寫成灰字（硬規則 4）；鈕在下面的收合區裡，要講出位置 */
                        <Banner kind="warn" icon={<MdErrorOutline />}>
                          審核已通過，但工項仍停留在「審核中」，未自動變更為已完成。
                          請展開下方「變更狀態與工時」，按「審核已通過，標記為已完成」。
                        </Banner>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {pctWillStart
                            ? "送出後將自動由「待處理」轉為「進行中」，無須手動變更狀態。"
                            : pctDrivesStatus
                              ? "無須手動變更狀態：進度大於 0 時送出將自動轉為「進行中」；選擇 100% 則自動送交審核人。"
                              : "此工項已在審核中：再次回報 100% 僅新增一筆紀錄，不會重新建立審核單。請等待審核人決議，審核通過後將自動完成，無須另行操作。"}
                        </Typography>
                      )}

                      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                        {/* 整頁唯一的主要按鈕（硬規則 1） */}
                        <Button variant="contained" type="button" data-tour="my-submit" onClick={submitReport}>
                          送出回報
                        </Button>
                        <input
                          ref={rFileRef}
                          type="file"
                          hidden
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            e.target.value = "";
                            if (f) setRFile(f);
                          }}
                        />
                        <Button
                          variant="outlined"
                          type="button"
                          startIcon={rFile ? undefined : <MdAdd aria-hidden="true" />}
                          onClick={() => rFileRef.current && rFileRef.current.click()}
                        >
                          {rFile ? "更換檔案" : "新增圖片或檔案"}
                        </Button>
                        {rFile && (
                          <>
                            <Typography variant="body2" color="text.secondary" component="span">
                              {rFile.name}
                            </Typography>
                            <Button variant="text" type="button" onClick={() => setRFile(null)}>
                              移除檔案
                            </Button>
                          </>
                        )}
                      </Stack>
                      {/* 設計稿這裡還寫了「今天之內還可以回來改」—— 系統沒有這個能力
                          （沒有任何端點改得了已經寫下的紀錄），所以那半句不照抄 */}
                      <Typography variant="body2" color="text.secondary">
                        送出後將自動產生今日紀錄（日報），主管即可檢視，無須重複填寫。
                      </Typography>

                      {/*
                        設計稿沒有位置的兩格：改狀態與工時。不刪，收進來。
                        預設收合是因為多數回報根本不必動它們（進度會自己把狀態推走）。
                      */}
                      <Disclosure
                        title="變更狀態與工時"
                        count={`（目前為${curLabel}）`}
                        data-tour="my-status"
                        open={moreOpen}
                        onToggle={setMoreOpen}
                        summaryExtra={foldText(moreOpen)}
                      >
                        <Stack spacing={3}>
                          <Box>
                            <Typography component="label" htmlFor="rep-status" sx={{ display: "block", fontWeight: 600, mb: 1 }}>
                              變更狀態為
                            </Typography>
                            <TextField
                              id="rep-status"
                              select
                              fullWidth
                              value={rStatus || curLabel}
                              onChange={(e) => setRStatus(e.target.value)}
                              slotProps={{ select: { native: true }, htmlInput: { "aria-describedby": "rep-status-help" } }}
                            >
                              {statusOpts.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </TextField>
                            <FormHelperText id="rep-status-help">
                              選項不含「已完成」：審核人核准後將自動變更為已完成，無須手動變更。
                            </FormHelperText>
                          </Box>

                          {/*
                            需要結論的轉換（關閉之類）才展開這一格。
                            結論是「為什麼收掉」——不予修復、重複、暫時不處理…，收進報表用的，
                            所以讓人選而不是系統代填。
                          */}
                          {needRes && (
                            <Box>
                              <Typography component="label" htmlFor="rep-resolution" sx={{ display: "block", fontWeight: 600, mb: 1 }}>
                                解決結論
                                <Typography component="span" color="error" variant="body2" sx={{ ml: 1 }}>
                                  必填
                                </Typography>
                              </Typography>
                              {resolutions.length > 0 ? (
                                <TextField
                                  id="rep-resolution"
                                  select
                                  fullWidth
                                  value={rResId}
                                  onChange={(e) => setRResId(e.target.value)}
                                  slotProps={{ select: { native: true } }}
                                >
                                  {resolutions.map((r) => (
                                    <option key={r.id} value={r.id}>
                                      {r.name}
                                    </option>
                                  ))}
                                </TextField>
                              ) : (
                                <FormHelperText error sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  <MdWarningAmber aria-hidden="true" />
                                  系統尚未設定任何解決結論，請先套用 V001 migration 的 resolution 種子資料。
                                </FormHelperText>
                              )}
                            </Box>
                          )}

                          {/* 只想推到下一站、不想寫回報時走這顆（改版前它在清單的每一列上） */}
                          {NEXT_LABEL[curKey] && (
                            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                              <Button variant="outlined" type="button" onClick={() => advance(selTask)}>
                                直接{NEXT_LABEL[curKey]}（不回報）
                              </Button>
                              <Typography variant="body2" color="text.secondary">
                                此按鈕僅變更狀態，不會留下「本次工作內容」。
                              </Typography>
                            </Stack>
                          )}

                          {/* 被退件卻停在審核中時的出口（理由見 rejectedButStuck），平常不會出現 */}
                          {rejectedButStuck && (
                            <Box>
                              <Button variant="outlined" type="button" onClick={() => advance(selTask, "progress")}>
                                退回進行中
                              </Button>
                            </Box>
                          )}

                          {/* 審核通過卻沒自動完成時唯一的出口（理由見 approvedButStuck），平常不會出現 */}
                          {approvedButStuck && (
                            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
                              <Button variant="outlined" type="button" onClick={() => advance(selTask, "done")}>
                                審核已通過，標記為已完成
                              </Button>
                              <Typography variant="body2" color="text.secondary">
                                若尚有未勾選的驗收條件，將無法變更並顯示未通過的項目。
                              </Typography>
                            </Stack>
                          )}

                          <Box>
                            <Typography component="label" htmlFor="rep-git" sx={{ display: "block", fontWeight: 600, mb: 1 }}>
                              程式碼位置
                            </Typography>
                            <TextField
                              id="rep-git"
                              fullWidth
                              value={rGit}
                              placeholder="main a1b2c3d"
                              onChange={(e) => setRGit(e.target.value)}
                              slotProps={{ htmlInput: { "aria-describedby": "rep-git-help" } }}
                            />
                            <FormHelperText id="rep-git-help">分支與 commit 以空白分隔；無則留空。</FormHelperText>
                          </Box>

                          {/*
                            工時：這一頁從以前到現在都沒有工時輸入格，POST /MyTask/Progress
                            也沒有工時參數（taskId / content / progress / gitBranch / gitCommit / file），
                            所以這裡只把已經登記的數字唸出來。

                            原本這裡有一顆「去工時管理登記」按到 /Timesheet。那一頁已經改成
                            「這一週每天記了什麼」，整個系統也沒有任何畫面會呼叫 POST /Timesheet/Entry
                            了（使用者：「時數沒屁用」），按過去只會找不到可以填的地方。
                            與其留一顆把人帶到死路的鈕，不如直接說清楚現在沒有登記的入口。
                          */}
                          <Stack spacing={1}>
                            <Typography variant="h3" component="h3">工時</Typography>
                            <Typography>
                              此工項已登記 {Number(selTask.act) || 0} 小時
                              {selTask.est ? `，預估 ${selTask.est} 小時` : ""}。
                              目前未提供工時登記畫面，此數字為既有紀錄。
                            </Typography>
                          </Stack>
                        </Stack>
                      </Disclosure>
                    </Stack>
                  </CardContent>
                </Card>
              )}

              {/* ---- 其他資料：預設收合，沒有內容的整個不畫（設計稿 L1222-L1257） ---- */}
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {showDod && (
                  <Disclosure
                    title="驗收條件"
                    count={`（${dod.length} 項，已完成 ${dodDone} 項）`}
                    data-tour="my-dod"
                    open={dodOpen}
                    onToggle={setDodOpen}
                    summaryExtra={foldText(dodOpen)}
                  >
                      {/*
                        勾選框改成整條可點的按鈕：原本是一個 20px 的小方塊，
                        而規則是「任何可點物件 ≥ 44px 且一定有文字」（硬規則 3）。

                        這不只是紀錄：還有沒勾的條件時，工作流的守門會把送審擋下來並回
                        「尚有驗收條件未完成」，選「做完了」的自動送審也一樣擋得到。
                      */}
                    <Stack spacing={1}>
                      {dod.map((c, i) => {
                        const done = !!(c.is_checked || c.isChecked);
                        return (
                          <Button
                            key={c.id != null ? c.id : i}
                            variant="outlined"
                            type="button"
                            fullWidth
                            aria-pressed={done}
                            onClick={() => toggleDod(c, i)}
                            startIcon={done ? <MdCheck aria-hidden="true" /> : <MdRadioButtonUnchecked aria-hidden="true" />}
                            /* 勾過的用次要文字色退一階（不畫刪除線：整句劃掉的長句子最難讀），
                               前面的 ✓／○ 才是主要的區別 */
                            sx={{
                              justifyContent: "flex-start",
                              textAlign: "start",
                              whiteSpace: "normal",
                              height: "auto",
                              py: 1.5,
                              ...(done ? { color: "text.secondary" } : {}),
                            }}
                          >
                            {c.content || c.text}
                          </Button>
                        );
                      })}
                    </Stack>
                  </Disclosure>
                )}

                {showLogs && (
                  <Disclosure
                    title="歷史紀錄"
                    count={!logsLoading && !logsError ? `（${reportLog.length} 筆）` : null}
                    open={logsOpen}
                    onToggle={setLogsOpen}
                    summaryExtra={foldText(logsOpen)}
                  >
                    {logsLoading && <SkeletonRows rows={3} />}
                    {/*
                      取失敗時不能顯示「還沒有任何紀錄」—— 那是在說資料不存在，
                      其實只是這次沒拿到。
                    */}
                    {!logsLoading && logsError && (
                      <FormHelperText error sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <MdWarningAmber aria-hidden="true" />
                        紀錄載入失敗，請重新整理頁面後再試。
                      </FormHelperText>
                    )}
                    {!logsLoading && !logsError && (
                      <Stack spacing={2}>
                        {reportLog.map((l, i) => (
                          // 歷程沒有穩定鍵值（同一秒可能多筆），以索引為鍵；清單僅重新載入時整批換掉
                          // eslint-disable-next-line react/no-array-index-key
                          <Stack key={i} spacing={1}>
                            <Typography variant="body2" color="text.secondary" component="div">
                              {l.time}
                              {l.meta ? `　${l.meta}` : ""}
                              {/* 依 Git 紀錄補登的歷史記錄，要看得出來不是當時親手回報的 */}
                              {l.backfill && (
                                <StatusChip kind="closed" mark={<MdRemove />} sx={{ ml: 1 }}>
                                  事後補登
                                </StatusChip>
                              )}
                            </Typography>
                            {/* 回報內容是 HTML（可含截圖），與「交代的事」用同一個 RichView */}
                            {l.body && (
                              <RichView
                                html={l.body}
                                sxStyle="font-size:var(--t-body);line-height:var(--t-body-lh);color:var(--ink);overflow-x:auto;"
                              />
                            )}
                            {l.empty && <Typography>更新回報</Typography>}
                            {l.hasFile && (
                              <Box>
                                <Button variant="text" type="button" onClick={() => downloadProgressFile(l)}>
                                  下載 {l.fileName || "附件"}
                                </Button>
                              </Box>
                            )}
                          </Stack>
                        ))}
                      </Stack>
                    )}
                  </Disclosure>
                )}

                {/*
                  附件（PM 傳上來的規格書、設計稿、截圖）。
                  這裡只給下載：要上傳或刪除請到完整工項，避免在回報流程中誤刪 PM 的檔案。
                */}
                {showAtts && (
                  <Disclosure
                    title="附件"
                    count={`（${atts.length} 個）`}
                    open={attOpen}
                    onToggle={setAttOpen}
                    summaryExtra={foldText(attOpen)}
                  >
                    <Stack spacing={1}>
                      {atts.map((a) => (
                        <Button
                          key={a.id}
                          variant="outlined"
                          type="button"
                          fullWidth
                          onClick={() => downloadAtt(a)}
                          sx={{ justifyContent: "flex-start", textAlign: "start", whiteSpace: "normal", height: "auto", py: 1.5, gap: 1 }}
                        >
                          下載 {a.file_name}
                          <Typography variant="body2" color="text.secondary" component="span">
                            {fmtAttSize(a.file_size)}
                          </Typography>
                        </Button>
                      ))}
                    </Stack>
                  </Disclosure>
                )}

                {/*
                  留言 —— 與工項詳情同一份資料（/Task/Comment）。
                  RD 在這一頁做事，回一句話卻要跳去工項詳情，來回幾次就沒人回了；
                  PM 的退件說明也常常留在留言裡，看不到等於白寫。
                */}
                {showCmt && (
                  <Disclosure
                    title="留言"
                    count={`（${comments.length} 則）`}
                    open={cmtOpen}
                    onToggle={setCmtOpen}
                    summaryExtra={foldText(cmtOpen)}
                  >
                    <Stack spacing={2}>
                      {comments
                        .slice()
                        .reverse()
                        .map((cm) => {
                          const actor = cm.create_name || cm.create_id || "";
                          return (
                            <Stack key={cm.id} spacing={1}>
                              <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                                <Typography sx={{ fontWeight: 600 }}>{actor}</Typography>
                                <Typography variant="body2" color="text.secondary" component="span">
                                  {fmtRelative(cm.create_at)}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                                {cm.id != null && (
                                  <Button variant="text" type="button" onClick={() => removeComment(cm)}>
                                    刪除
                                  </Button>
                                )}
                              </Stack>
                              {/* mentionHtml 會先跳脫再把「@名字」上色，內容是使用者輸入的字 */}
                              <Typography component="div" dangerouslySetInnerHTML={{ __html: mentionHtml(cm.content) }} />
                            </Stack>
                          );
                        })}

                      <Box>
                        <Typography component="label" htmlFor="rep-cmt" sx={{ display: "block", fontWeight: 600, mb: 1 }}>
                          新增留言
                        </Typography>
                          {/*
                          提示不要寫「可用 @ 提及成員」：後端 TaskService.PostTaskComment
                          沒有任何 @ 解析，被提到的人不會收到指名給他的東西。
                        */}
                        <TextField
                          placeholder="輸入留言，可使用 @ 標示同事"
                          id="rep-cmt"
                          inputRef={cmtRef}
                          multiline
                          minRows={2}
                          fullWidth
                          value={commentDraft}
                          onChange={(e) => setCommentDraft(e.target.value)}
                          onKeyDown={(e) => {
                            /*
                              改版前這格是單行輸入，Enter 就送出。換成多行是為了
                              寫得下多行，但那個送出捷徑不能跟著不見 —— 習慣打完就按
                              Enter 的人會以為留言送出去了。所以改綁 Ctrl/⌘+Enter：
                              Enter 留給換行，捷徑還在，兩種人都不會被這次改版弄到。
                            */
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              addComment();
                            }
                          }}
                        />
                        <FormHelperText>可按 Ctrl+Enter 送出。</FormHelperText>
                      </Box>
                      <Box>
                        <Button variant="outlined" type="button" onClick={addComment}>
                          送出留言
                        </Button>
                      </Box>
                    </Stack>
                  </Disclosure>
                )}

                {/* 還沒有的東西只留文字入口，不畫一塊空的區域（硬規則 2、設計稿 L1257） */}
                {(!showCmt || !showDod || !showAtts) && (
                  <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center", pt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" component="span">
                      尚無資料：
                    </Typography>
                    {!showCmt && (
                      <Button
                        variant="text"
                        type="button"
                        startIcon={<MdAdd aria-hidden="true" />}
                        onClick={() => {
                          setCmtOpen(true);
                          window.setTimeout(() => cmtRef.current && cmtRef.current.focus(), 0);
                        }}
                      >
                        新增留言
                      </Button>
                    )}
                    {(!showDod || !showAtts) && (
                      /* 這一頁只讀得到驗收條件與附件，加是在完整工項那邊做的（避免在回報
                         流程中誤刪 PM 的檔案），所以按鈕直接說「到完整工項加」，不假裝這裡能加 */
                      <Button
                        variant="text"
                        type="button"
                        startIcon={<MdAdd aria-hidden="true" />}
                        onClick={() => openDetail(selTask)}
                      >
                        {!showDod && !showAtts ? "驗收條件、附件" : !showDod ? "驗收條件" : "附件"}
                        （至完整工項新增）
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            </>
          )}
        </Box>
      </Box>
      )}

      {/* ---------- 今天的紀錄：唯讀，只是讓人確認今天記到了什麼（設計稿 L1273-L1300） ---------- */}
      {/* 沒有選著的工項時不畫：這張卡列的是選著那一件今天的紀錄，沒有可列的就只剩一句「去選一件」 */}
      {selTask && (todayLog.length > 0 || todayReported > 0) && (
        <Card component="section" sx={{ mt: 4 }}>
          <CardContent>
            <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="h3" component="h2">今日紀錄</Typography>
              {todayReported > 0 && (
                <StatusChip kind="done" mark={<MdCheck />}>
                  {todayReported} 筆
                </StatusChip>
              )}
              <Box sx={{ flex: 1 }} />
              {/* 「看這一週」導到工時管理：那是目前唯一一頁把一整週的工作攤開來看的畫面 */}
              <Button variant="text" type="button" onClick={() => navigate("/Timesheet")}>
                檢視本週
              </Button>
            </Stack>
            <Typography sx={{ mt: 1 }}>
              您今日於工項回報的內容將自動彙整為以下紀錄，即主管檢視的日報，無須重複填寫。
            </Typography>

            {todayLog.length > 0 && selTask && (
              <Box sx={{ mt: 2, mx: -2, borderTop: 1, borderColor: "divider" }}>
                {todayLog.map((l, i) => (
                  // 紀錄沒有穩定鍵值（同一秒可能多筆），以索引為鍵
                  // eslint-disable-next-line react/no-array-index-key
                  <Box
                    key={`t-${i}`}
                    sx={{
                      px: 3,
                      py: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      "&:not(:last-of-type)": { borderBottom: 1, borderColor: "divider" },
                    }}
                  >
                    {/* 這一列不是按鈕（日報是唯讀的），裡面要放 RichView 交出來的區塊內容 */}
                    <Typography variant="subtitle1" component="span">
                      {selTask.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      component="span"
                      sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 0.5 }}
                    >
                      <span>{fmtClock(l.at)}</span>
                      {selTask.projectName ? <span>{selTask.projectName}</span> : null}
                      {l.meta ? <span>{l.meta}</span> : null}
                    </Typography>
                    {l.body && (
                      <RichView
                        html={l.body}
                        sxStyle="font-size:var(--t-body);line-height:var(--t-body-lh);color:var(--ink);overflow-x:auto;"
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/*
              設計稿每一列右邊有一顆「改這句」，這裡沒有畫：後端沒有任何端點改得了
              已經寫下的進度紀錄（MyTaskController 只有 List / Progress(PUT,POST) /
              Progress/{taskId} / Today / Progress/Download），POST 只會再寫一筆新的。
              要更正就再回報一次，舊的那筆會留著 —— 下面那句話講的就是這件事。

              件數與列數對不上也要講清楚：徽章上的數字是今天跨所有工項的回報筆數
              （GET /MyTask/Today 算的），但「今天所有工項的回報內容」沒有對應的清單端點，
              /MyTask/Progress/{taskId} 一次只給一件，所以下面只列得出目前選著的那一件。
            */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {todayLog.length === 0
                ? "今日的回報位於其他工項，選擇該工項即可檢視。"
                : todayReported > todayLog.length
                  ? `上方僅列出「${selTask ? selTask.name : ""}」的紀錄；今日另有 ${todayReported - todayLog.length} 筆位於其他工項，選擇該工項即可檢視。`
                  : "已送出的紀錄無法修改；如需更正，請重新回報，原紀錄將保留。"}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/*
        抽屜掛在最外層而不是右欄裡 —— 它本身會 portal 到 body，但掛在會被條件
        渲染掉的節點下（右欄在沒選工項時不渲染）會連同關閉動作一起消失。
        onChanged 除了重抓清單，也把右欄正在看的那一筆的驗收條件與紀錄重載，
        否則在抽屜裡改完狀態，背後的回報面板還停在舊資料。
      */}
      <TaskDrawer
        open={!!drawerTask}
        taskId={drawerTask?.id}
        task={drawerTask}
        projectName={drawerTask?.projectName}
        onClose={() => setDrawerTask(null)}
        onChanged={() => {
          reload();
          if (selId) {
            loadReview(selId);
            loadLogs(selId);
          }
        }}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  /* 與 Layout 的側欄紅點同一套取法，兩邊件數才一致 */
  const toMe = _.get(state, "Approval.ToMe");
  const list = [toMe, _.get(toMe, "data_list"), _.get(toMe, "list"), _.get(toMe, "items")].find(Array.isArray) || [];
  return {
    myTasks: _.get(state, "MyTask.List", []),
    approvalsCount: list.length,
    toMeRaw: toMe,
  };
};

const mapDispatchToProps = (dispatch) => ({
  GET_MyTaskList: (payload, callback, loading) =>
    dispatch({ type: "GET_MyTaskList", payload, callback, loading }),
  GET_ApprovalToMe: (payload, callback, loading) =>
    dispatch({ type: "GET_ApprovalToMe", payload, callback, loading }),
});

App.propTypes = {
  myTasks: PropTypes.array,
  approvalsCount: PropTypes.number,
  toMeRaw: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  GET_MyTaskList: PropTypes.func,
  GET_ApprovalToMe: PropTypes.func,
};

App.defaultProps = {
  myTasks: [],
  approvalsCount: 0,
  toMeRaw: [],
  GET_MyTaskList: () => {},
  GET_ApprovalToMe: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
